import type { Command } from "../document/commands.ts";
import type { MapDocument, PropBehaviour, PropInstance, TileRef, WorldDocument } from "../document/types.ts";
import { propsInRect, together } from "./many-props.ts";
import { changePropCommand } from "./panel-commands.ts";
import type { FrameSizeOf } from "./prop-hit.ts";
import type { ReplaceScope } from "./replace.ts";
import type { PropRef } from "./templates.ts";

/**
 * Replace in selection for props, the pure part (ADR-097's amendment of 2026-10-12, decisions 12 to 20): which
 * props an area holds, by the marquee's own rule, what they place and carry, and one `updateProp` for each prop
 * that takes the frame in hand. A plain prop is replaced by its frame, mirrored or not; a prop with a behaviour
 * only by its type, and only when the game suggests a frame from the new one for every state and animation it
 * named. A replaced prop keeps its id, layer, place in the list, position, mirroring, sort offset and mark, and
 * loses its own footprint as any change of frame does (`changePropCommand`).
 */

/**
 * A frame placed by props with no behaviour in the area, mirrored or not, counted in props, with the props
 * carrying a behaviour that place it and are passed over.
 */
export interface ReplacePropFrameSource {
  readonly kind: "propFrame";
  readonly tileset: string;
  readonly tile: number;
  readonly count: number;
  readonly passed: number;
}

/**
 * A behaviour type carried by props in the area, counted in props.
 */
export interface ReplacePropTypeSource {
  readonly kind: "propType";
  readonly type: string;
  readonly count: number;
}

/**
 * One prop line of the "from" list.
 */
export type ReplacePropSource = ReplacePropFrameSource | ReplacePropTypeSource;

/**
 * What a prop replace needs from the editor: a frame's size, the tile size and the frame the map shows a prop
 * with (`frameOf`, the state previews' own; a prop's resting frame when absent), which place a prop's art as the
 * marquee does; and the game's suggestions from a frame, resolved against the catalogue, for a type's state
 * (`suggestedStateFrame`) and for its animations (`suggestedAnimations`). Lines and matches read a prop's own
 * frame, never the one shown.
 */
export interface ReplacePropEnv {
  readonly sizeOf: FrameSizeOf;
  readonly tileSize: number;
  readonly frameOf?: (layerId: string, prop: PropInstance) => TileRef;
  readonly stateFrame: (type: string, stateId: string, placed: TileRef) => TileRef | undefined;
  readonly animations: (type: string, placed: TileRef) => Readonly<Record<string, TileRef>>;
}

/**
 * What to replace with what: the ticked frames, whose plain props take the frame in hand, and the ticked
 * types, whose props take it with their state frames and animations suggested from it. The hand's mirroring
 * is ignored.
 */
export interface ReplacePropPicks {
  readonly frames: readonly { readonly tileset: string; readonly tile: number }[];
  readonly types: readonly string[];
  readonly to: TileRef;
}

/**
 * A prop of a ticked type left as it is, and why.
 */
export interface ReplacePropSkip {
  readonly layerId: string;
  readonly propId: string;
  readonly why: "no frame for its states";
}

/**
 * A prop replace: its command (null when every ticked prop was skipped), the props it changes, the props of a
 * ticked type it skipped, and the layers it writes in the map's order.
 */
export interface ReplacePropPlan {
  readonly command: Command | null;
  readonly props: number;
  readonly skipped: readonly ReplacePropSkip[];
  readonly layers: readonly string[];
}

/**
 * What reads which props an area holds: frame sizes, the tile size and the frame each prop is shown with.
 */
type AreaEnv = Pick<ReplacePropEnv, "sizeOf" | "tileSize" | "frameOf">;

/**
 * The props an area holds on the prop layers in scope, in layer order and each layer's order: with a rectangle
 * of tiles, those whose art, as the map shows it, lies wholly inside it as the marquee has it (`propsInRect`
 * with `frameOf`); with none, every prop of those layers.
 *
 * @param map The map.
 * @param scope The area, in tiles, and the layers.
 * @param env Frame sizes, the tile size and the frame each prop is shown with.
 * @returns The props.
 */
export function propsInArea(map: MapDocument, scope: ReplaceScope, env: AreaEnv): PropRef[] {
  const wanted = new Set(scope.layerIds);
  const rect = scope.rect;
  if (rect === null) {
    return map.layers.flatMap((layer) => (layer.kind === "prop" && wanted.has(layer.id) ? layer.props.map((prop) => ({ layerId: layer.id, propId: prop.id })) : []));
  }
  const size = env.tileSize;
  const box = { left: rect.x0 * size, top: rect.y0 * size, right: (rect.x1 + 1) * size, bottom: (rect.y1 + 1) * size };
  return propsInRect(map, (layerId) => wanted.has(layerId), env.sizeOf, size, box, env.frameOf === undefined ? {} : { frameOf: env.frameOf });
}

/**
 * The props an area holds, each with its layer.
 *
 * @param map The map.
 * @param scope The area and the layers.
 * @param env Frame sizes, the tile size and the frame each prop is shown with.
 * @returns The props, in layer order and each layer's order.
 */
function heldProps(map: MapDocument, scope: ReplaceScope, env: AreaEnv): { readonly layerId: string; readonly prop: PropInstance }[] {
  const held = new Set(propsInArea(map, scope, env).map((ref) => `${ref.layerId}\u0000${ref.propId}`));
  return map.layers.flatMap((layer) => (layer.kind === "prop" ? layer.props.filter((prop) => held.has(`${layer.id}\u0000${prop.id}`)).map((prop) => ({ layerId: layer.id, prop })) : []));
}

/**
 * The key of a frame, a mirror aside.
 *
 * @param frame The frame.
 * @returns The key.
 */
function frameKey(frame: { readonly tileset: string; readonly tile: number }): string {
  return `${frame.tileset}\u0000${String(frame.tile)}`;
}

/**
 * Orders two strings by code unit.
 *
 * @param a One.
 * @param b The other.
 * @returns Negative, zero or positive.
 */
function byText(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Lists the props an area holds: one line for each frame a prop with no behaviour places, mirrored or not, with
 * the props carrying a behaviour that place it counted as passed over; then one line for each behaviour type.
 * Each kind is most used first, then by tileset and tile or by type. A frame only behaviour props place has
 * no frame line.
 *
 * @param map The map.
 * @param scope The area, in tiles, and the layers.
 * @param env Frame sizes, the tile size and the frame each prop is shown with.
 * @returns The lines; none when the area holds no prop on those layers.
 */
export function scanReplaceProps(map: MapDocument, scope: ReplaceScope, env: AreaEnv): ReplacePropSource[] {
  const frames = new Map<string, { tileset: string; tile: number; count: number; passed: number }>();
  const types = new Map<string, number>();
  for (const { prop } of heldProps(map, scope, env)) {
    const key = frameKey(prop.frame);
    const line = frames.get(key) ?? { tileset: prop.frame.tileset, tile: prop.frame.tile, count: 0, passed: 0 };
    frames.set(key, line);
    if (prop.behaviour === undefined) {
      line.count += 1;
    } else {
      line.passed += 1;
      types.set(prop.behaviour.type, (types.get(prop.behaviour.type) ?? 0) + 1);
    }
  }
  const frameLines = [...frames.values()]
    .filter((line) => line.count > 0)
    .sort((a, b) => b.count - a.count || byText(a.tileset, b.tileset) || a.tile - b.tile)
    .map((line): ReplacePropFrameSource => ({ kind: "propFrame", ...line }));
  const typeLines = [...types.entries()].sort((a, b) => b[1] - a[1] || byText(a[0], b[0])).map(([type, count]): ReplacePropTypeSource => ({ kind: "propType", type, count }));
  return [...frameLines, ...typeLines];
}

/**
 * The behaviour a prop of a ticked type takes with the new frame: its type, fields and activation cells kept,
 * each state frame it names the one suggested for that state from the new frame, each animation it names the
 * one suggested from it.
 *
 * @param behaviour The prop's behaviour.
 * @param to The new frame.
 * @param env The game's suggestions.
 * @returns The behaviour, or null when a state or an animation it named has no suggestion.
 */
function reframedBehaviour(behaviour: PropBehaviour, to: TileRef, env: ReplacePropEnv): PropBehaviour | null {
  let frames: Record<string, TileRef> | undefined;
  if (behaviour.frames !== undefined) {
    frames = {};
    for (const stateId of Object.keys(behaviour.frames)) {
      const suggested = env.stateFrame(behaviour.type, stateId, to);
      if (suggested === undefined) {
        return null;
      }
      frames[stateId] = suggested;
    }
  }
  let animations: Record<string, TileRef> | undefined;
  if (behaviour.animations !== undefined) {
    const offered = env.animations(behaviour.type, to);
    animations = {};
    for (const name of Object.keys(behaviour.animations)) {
      const suggested = offered[name];
      if (suggested === undefined) {
        return null;
      }
      animations[name] = suggested;
    }
  }
  return { ...behaviour, ...(frames === undefined ? {} : { frames }), ...(animations === undefined ? {} : { animations }) };
}

/**
 * Plans a prop replace: one `updateProp` for each prop in the area that changes, in layer order and each
 * layer's order, joined by `together`. A prop with no behaviour of a ticked frame, mirrored or not, takes the
 * frame in hand; a prop of a ticked type takes it with its state frames and animations suggested from it, or is
 * skipped and named when one has no suggestion. Every change is `changePropCommand`'s change of frame, which
 * drops the prop's own footprint; the hand's mirroring is ignored, and a prop already showing the frame in hand
 * adds nothing.
 *
 * @param world The world.
 * @param mapId The map replaced on.
 * @param scope The area, in tiles, and the layers.
 * @param picks What to replace with what.
 * @param env Frame sizes, the tile size and the game's suggestions.
 * @returns The plan, or null when nothing would change and nothing was skipped.
 * @throws Error when the map is missing or the frame in hand names a tileset the world does not hold.
 */
export function planReplaceProps(world: WorldDocument, mapId: string, scope: ReplaceScope, picks: ReplacePropPicks, env: ReplacePropEnv): ReplacePropPlan | null {
  const map = world.maps.find((m) => m.id === mapId);
  if (map === undefined) {
    throw new Error(`map ${mapId} does not exist`);
  }
  if (!world.tilesets.some((tileset) => tileset.id === picks.to.tileset)) {
    throw new Error(`the frame in hand names tileset ${picks.to.tileset}, which the world does not hold`);
  }
  const to: TileRef = { tileset: picks.to.tileset, tile: picks.to.tile };
  const frames = new Set(picks.frames.map(frameKey));
  const types = new Set(picks.types);
  const commands: Command[] = [];
  const skipped: ReplacePropSkip[] = [];
  const layers: string[] = [];
  for (const { layerId, prop } of heldProps(map, scope, env)) {
    const behaviour = prop.behaviour;
    const ticked = behaviour === undefined ? frames.has(frameKey(prop.frame)) : types.has(behaviour.type);
    if (!ticked || frameKey(prop.frame) === frameKey(to)) {
      continue;
    }
    let command: Command;
    if (behaviour === undefined) {
      command = changePropCommand(map, layerId, prop.id, { frame: to });
    } else {
      const next = reframedBehaviour(behaviour, to, env);
      if (next === null) {
        skipped.push({ layerId, propId: prop.id, why: "no frame for its states" });
        continue;
      }
      command = changePropCommand(map, layerId, prop.id, { frame: to, behaviour: next });
    }
    commands.push(command);
    if (!layers.includes(layerId)) {
      layers.push(layerId);
    }
  }
  if (commands.length === 0 && skipped.length === 0) {
    return null;
  }
  return { command: together(commands), props: commands.length, skipped, layers };
}
